// piano-frontend/src/pages/admin/OperationsTeacherSchedule.js
// 운영 설정 — 선생님 스케줄 설정(기본) 탭 (PC 전용). 캘린더 캔버스 방식(블록 탭과 동일 문법).
// 전체 보기: 요일마다 근무가 있는 선생님끼리만 레인을 나눠 이름표 블록(옅은 틴트 + 왼쪽 컬러 바)을 나란히 표시.
// 선생님 선택 시: 그 선생님 블록만 전체 폭으로 표시하고 위/아래 그립 핸들 드래그(30분 스냅) 또는
// 좌측 패널에서 요일별 근무창을 추가/삭제/조정한다. 저장 전 수정분은 빗금 + '저장 전' 배지로 표시.
// 저장은 기존 PUT /teachers/:id를 사용하며, 회원 스케줄/예약 충돌(409) 시 '무시하고 저장'으로 이어진다.
// 특정 날짜의 휴가/시간 조정(변동 스케줄)은 운영 시간 설정(예외) 탭에서 관리한다.
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Stack, Paper, Button, Chip, IconButton, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Tooltip, Alert
} from "@mui/material";
import { Add, Delete, KeyboardArrowUp } from "@mui/icons-material";
import api from "../../api/axios";
import { useDarkMode } from "../../App";
import { useDialog } from "../../contexts/DialogProvider";
import { TEACHER_PALETTE } from "./OperationsBlockSchedule";

const TIME_OPTIONS = (() => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      options.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return options;
})();

const DAY_CODES = [["MON", "월"], ["TUE", "화"], ["WED", "수"], ["THU", "목"], ["FRI", "금"], ["SAT", "토"]];

// 축 기본 시작은 10:00 — 그 이전 시간대는 '이전 시간 보기'로 확장해서 본다.
// 10:00 이전에 시작하는 스케줄이 있으면 자동으로 그 시간까지 내려서 보여준다.
const AXIS_MIN = 0;
const AXIS_DEFAULT_START = 10 * 60;
const AXIS_END = 23 * 60 + 30;
const SLOT_H = 24;

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");
const toMin = (t) => {
  const [h, m] = String(t).slice(0, 5).split(":").map(Number);
  return h * 60 + m;
};
const minToTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function TimeSelect({ label, value, onChange, width = 96 }) {
  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel shrink>{label}</InputLabel>
      <Select value={value || ""} label={label} onChange={(e) => onChange(e.target.value)} notched>
        {TIME_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

function SaveBar({ dirty, saving, onSave, onReset }) {
  if (!dirty) return null;
  return (
    <Box sx={{
      position: "sticky", bottom: 0, mt: 2, mx: -2, px: 2, py: 1,
      bgcolor: "background.paper", borderTop: 1, borderColor: "divider",
      display: "flex", alignItems: "center", gap: 1, zIndex: 5
    }}>
      <Typography variant="caption" color="warning.main" fontWeight={600}>
        저장되지 않은 변경 사항이 있습니다.
      </Typography>
      <Box flex={1} />
      <Button size="small" onClick={onReset} disabled={saving}>되돌리기</Button>
      <Button size="small" variant="contained" onClick={onSave} disabled={saving}>
        {saving ? "저장 중..." : "저장"}
      </Button>
    </Box>
  );
}

export default function OperationsTeacherSchedule() {
  const { darkMode } = useDarkMode();
  const { confirm, toast } = useDialog();
  const [teachers, setTeachers] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [working, setWorking] = useState([]);      // 선택 선생님의 편집 중 스케줄 [{day,start,end}]
  const [snapshot, setSnapshot] = useState("[]");
  const [saving, setSaving] = useState(false);
  // 블록 가장자리 드래그: { index, edge, startY, origMin }
  const [drag, setDrag] = useState(null);
  // 수동 축 시작(이전 시간 보기로 확장). 실제 축 시작은 데이터에 따라 더 내려갈 수 있다.
  const [axisStart, setAxisStart] = useState(AXIS_DEFAULT_START);
  const axisStartRef = useRef(AXIS_DEFAULT_START);

  const pal = useCallback((idx) => {
    const p = TEACHER_PALETTE[idx % TEACHER_PALETTE.length];
    const m = darkMode ? 1 : 0;
    return { fill: p.fill[m], bar: p.bar[m], text: p.text[m] };
  }, [darkMode]);

  const fetchTeachers = useCallback(async () => {
    try {
      const res = await api.get("/teachers");
      const list = res.data.map((t) => ({
        ...t,
        schedules: (t.schedules || []).map((s) => ({ day: s.day, start: hhmm(s.start), end: hhmm(s.end) }))
      }));
      setTeachers(list);
      return list;
    } catch (err) {
      console.error("선생님 목록 조회 실패:", err);
      setTeachers([]);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  // 드래그 (30분 스냅)
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const deltaSlots = Math.round((e.clientY - drag.startY) / SLOT_H);
      const newMin = drag.origMin + deltaSlots * 30;
      setWorking((prev) => {
        const w = prev[drag.index];
        if (!w) return prev;
        const s = toMin(w.start);
        const en = toMin(w.end);
        if (drag.edge === "start") {
          const clamped = Math.max(axisStartRef.current, Math.min(newMin, en - 30));
          if (clamped === s) return prev;
          return prev.map((item, i) => (i === drag.index ? { ...item, start: minToTime(clamped) } : item));
        }
        const clamped = Math.min(AXIS_END, Math.max(newMin, s + 30));
        if (clamped === en) return prev;
        return prev.map((item, i) => (i === drag.index ? { ...item, end: minToTime(clamped) } : item));
      });
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag]);

  const selectTeacher = (id) => {
    if (id === selectedId) return;
    const t = teachers.find((x) => x.id === id);
    setSelectedId(id);
    const copy = t ? JSON.parse(JSON.stringify(t.schedules)) : [];
    setWorking(copy);
    setSnapshot(JSON.stringify(copy));
  };

  const clearSelection = () => {
    setSelectedId(null);
    setWorking([]);
    setSnapshot("[]");
  };

  const dirty = selectedId != null && JSON.stringify(working) !== snapshot;
  const resetWorking = () => setWorking(JSON.parse(snapshot));

  const updateWindow = (index, patch) => {
    setWorking((prev) => prev.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  };
  const removeWindow = (index) => {
    setWorking((prev) => prev.filter((_, i) => i !== index));
  };
  const addWindow = (day) => {
    setWorking((prev) => [...prev, { day, start: "14:00", end: "18:00" }]);
  };

  async function save(force = false) {
    const teacher = teachers.find((t) => t.id === selectedId);
    if (!teacher) return;
    if (working.length === 0) {
      toast("근무 스케줄을 최소 1개 이상 등록해주세요.", "warning");
      return;
    }
    for (const w of working) {
      if (w.start >= w.end) {
        toast("시작 시간은 종료 시간보다 빨라야 합니다.", "warning");
        return;
      }
    }
    setSaving(true);
    try {
      await api.put(`/teachers/${selectedId}`, { name: teacher.name, schedules: working, force });
      toast("근무 스케줄이 저장되었습니다.");
      const list = await fetchTeachers();
      const updated = list.find((t) => t.id === selectedId);
      const copy = updated ? JSON.parse(JSON.stringify(updated.schedules)) : [];
      setWorking(copy);
      setSnapshot(JSON.stringify(copy));
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.requiresForce) {
        const conflicts = err.response.data.conflicts || [];
        const lines = conflicts.slice(0, 10).map((c) =>
          c.type === "SCHEDULE"
            ? `• [고정 스케줄] ${c.member_name} — ${c.day} ${c.time}`
            : `• [레슨 예약] ${c.member_name} — ${c.date} ${c.time}`
        );
        if (conflicts.length > 10) lines.push(`• 외 ${conflicts.length - 10}건`);
        const proceed = await confirm({
          title: "근무 스케줄 충돌 경고",
          severity: "warning",
          message:
            `변경하려는 근무 시간 밖에 있는 회원 스케줄/예약이 ${conflicts.length}건 있습니다.\n\n` +
            lines.join("\n") +
            `\n\n이대로 저장하면 해당 스케줄/예약은 근무 시간 밖에 그대로 남습니다.\n계속하시겠습니까?`,
          confirmText: "무시하고 저장",
          color: "warning"
        });
        if (proceed) {
          await save(true);
          return;
        }
      } else {
        toast(err?.response?.data?.message || "저장 실패", "error");
      }
    } finally {
      setSaving(false);
    }
  }

  if (!teachers) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const gridLineCss = {
    backgroundImage: `repeating-linear-gradient(to bottom, ${darkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"} 0 1px, transparent 1px ${SLOT_H * 2}px)`
  };

  // 유효 축 시작: 수동 확장값과 스케줄 최소 시작 시각 중 이른 쪽 (정시 스냅)
  const allStarts = [
    ...teachers.flatMap((t) => t.schedules.map((w) => toMin(w.start))),
    ...(selectedId != null ? working.map((w) => toMin(w.start)) : [])
  ];
  const dataStart = allStarts.length > 0 ? Math.floor(Math.min(...allStarts) / 60) * 60 : AXIS_DEFAULT_START;
  const effStart = Math.max(AXIS_MIN, Math.min(axisStart, dataStart));
  axisStartRef.current = effStart;
  const yOf = (min) => ((min - effStart) / 30) * SLOT_H;
  const axisHeight = ((AXIS_END - effStart) / 30) * SLOT_H;

  const stripe = darkMode ? "rgba(0,0,0,0.30)" : "rgba(255,255,255,0.45)";
  const savedKeys = new Set(JSON.parse(snapshot).map((w) => `${w.day}|${w.start}|${w.end}`));
  const selIdx = teachers.findIndex((t) => t.id === selectedId);
  const selName = selIdx >= 0 ? teachers[selIdx].name : "";
  const selPal = pal(Math.max(selIdx, 0));

  // 전체 보기: 해당 요일에 근무가 있는 선생님끼리만 레인을 나눠 나란히 배치
  const renderOverviewDay = (code) => {
    const lanes = teachers
      .map((t, idx) => ({ t, idx }))
      .filter(({ t }) => t.schedules.some((w) => w.day === code));
    const k = lanes.length;
    return lanes.flatMap(({ t, idx }, lane) => {
      const p = pal(idx);
      return t.schedules
        .filter((w) => w.day === code)
        .map((w) => {
          const start = toMin(w.start);
          const end = toMin(w.end);
          const top = yOf(Math.max(start, effStart)) + 1;
          const bottom = yOf(Math.min(end, AXIS_END));
          const height = Math.max(bottom - top - 1, SLOT_H - 2);
          return (
            <Tooltip key={`${t.id}-${w.start}`} title={`${t.name} ${w.start}~${w.end} — 클릭하면 편집`} arrow enterDelay={300}>
              <Box
                onClick={() => selectTeacher(t.id)}
                sx={{
                  position: "absolute",
                  left: `calc(${(lane / k) * 100}% + 2px)`,
                  width: `calc(${(1 / k) * 100}% - 5px)`,
                  top, height,
                  borderRadius: "6px", boxSizing: "border-box", overflow: "hidden",
                  bgcolor: p.fill, borderLeft: `3px solid ${p.bar}`,
                  color: p.text, px: 0.7, pt: "2px",
                  cursor: "pointer",
                  "&:hover": { outline: `1.5px solid ${p.bar}` }
                }}>
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.15, whiteSpace: "nowrap" }}>
                  {t.name}
                </Typography>
                {height >= SLOT_H * 2 - 4 && (
                  <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.2, whiteSpace: "nowrap", color: p.bar }}>
                    {k >= 2 ? <>{w.start}~<br />{w.end}</> : `${w.start}~${w.end}`}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        });
    });
  };

  // 편집 모드: 선택 선생님의 working 블록만 전체 폭으로. 저장 전 수정분은 빗금 + 배지.
  const renderEditDay = (code) =>
    working
      .map((w, wi) => ({ ...w, wi }))
      .filter((w) => w.day === code)
      .map((w) => {
        const start = toMin(w.start);
        const end = toMin(w.end);
        const top = yOf(Math.max(start, effStart)) + 1;
        const bottom = yOf(Math.min(end, AXIS_END));
        const height = Math.max(bottom - top - 1, SLOT_H - 2);
        const modified = !savedKeys.has(`${w.day}|${w.start}|${w.end}`);
        return (
          <Tooltip key={w.wi} title={`${w.start}~${w.end} — 위/아래 가장자리를 드래그해 조절`} arrow enterDelay={600}>
            <Box sx={{
              position: "absolute", left: 3, right: 3, top, height,
              borderRadius: "6px", boxSizing: "border-box", overflow: "hidden",
              bgcolor: selPal.fill, borderLeft: `3px solid ${selPal.bar}`,
              backgroundImage: modified ? `repeating-linear-gradient(45deg, ${stripe} 0 4px, transparent 4px 9px)` : "none",
              color: selPal.text,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"
            }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, lineHeight: 1.15, whiteSpace: "nowrap" }}>
                {selName}
              </Typography>
              {height >= SLOT_H * 2 - 4 && (
                <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.2, whiteSpace: "nowrap", color: selPal.bar }}>
                  {w.start}~{w.end}
                </Typography>
              )}
              {modified && (
                <Box sx={{
                  position: "absolute", top: 4, right: 4, px: 0.6, py: "1px",
                  borderRadius: "8px", bgcolor: selPal.bar, color: "#fff",
                  fontSize: "0.7rem", fontWeight: 600, lineHeight: 1.4
                }}>
                  저장 전
                </Box>
              )}
              <Box
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDrag({ index: w.wi, edge: "start", startY: e.clientY, origMin: start });
                }}
                sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 9,
                  cursor: "ns-resize", display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                <Box component="span" sx={{ width: 22, height: 3, borderRadius: 2, bgcolor: selPal.bar, opacity: 0.75 }} />
              </Box>
              <Box
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDrag({ index: w.wi, edge: "end", startY: e.clientY, origMin: end });
                }}
                sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 9,
                  cursor: "ns-resize", display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                <Box component="span" sx={{ width: 22, height: 3, borderRadius: 2, bgcolor: selPal.bar, opacity: 0.75 }} />
              </Box>
            </Box>
          </Tooltip>
        );
      });

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
      {/* 선생님 선택 칩 */}
      <Stack direction="row" spacing={0.8} alignItems="center" mb={1.5} useFlexGap flexWrap="wrap">
        <Chip
          label="전체 보기"
          clickable
          color={selectedId == null ? "primary" : "default"}
          variant={selectedId == null ? "filled" : "outlined"}
          onClick={clearSelection}
        />
        {teachers.map((t, idx) => (
          <Chip
            key={t.id}
            label={`${t.name} 선생님`}
            clickable
            variant={selectedId === t.id ? "filled" : "outlined"}
            onClick={() => selectTeacher(t.id)}
            sx={{
              borderColor: pal(idx).bar,
              color: selectedId === t.id ? "#fff" : pal(idx).bar,
              bgcolor: selectedId === t.id ? pal(idx).bar : "transparent",
              fontWeight: 600,
              "&:hover": { bgcolor: selectedId === t.id ? pal(idx).bar : undefined }
            }}
          />
        ))}
        <Box flex={1} />
        <Typography variant="caption" color="text.secondary">
          특정 날짜의 휴가·시간 조정은 운영 시간 설정(예외) 탭의 변동 스케줄로 등록하세요.
        </Typography>
      </Stack>

      {teachers.length === 0 ? (
        <Alert severity="info">등록된 선생님이 없습니다.</Alert>
      ) : (
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* 선택 시 좌측 편집 패널 */}
          {selectedId != null && (
            <Box sx={{ width: 290, flexShrink: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                {teachers.find((t) => t.id === selectedId)?.name} 선생님 근무 스케줄
              </Typography>
              {DAY_CODES.map(([code, label]) => {
                const rows = working
                  .map((w, i) => ({ ...w, i }))
                  .filter((w) => w.day === code);
                return (
                  <Paper key={code} variant="outlined" sx={{ p: 1, mb: 0.8 }}>
                    <Stack direction="row" alignItems="center" mb={rows.length > 0 ? 0.8 : 0}>
                      <Typography variant="body2" fontWeight={600} sx={{ width: 40 }}>{label}</Typography>
                      <Box flex={1} />
                      <Button size="small" startIcon={<Add sx={{ fontSize: 14 }} />} onClick={() => addWindow(code)}
                        sx={{ fontSize: "0.78rem", py: 0 }}>
                        근무 추가
                      </Button>
                    </Stack>
                    {rows.map((w) => (
                      <Stack key={w.i} direction="row" spacing={0.8} alignItems="center" mb={0.5}>
                        <TimeSelect label="시작" value={w.start} onChange={(v) => updateWindow(w.i, { start: v })} />
                        <TimeSelect label="종료" value={w.end} onChange={(v) => updateWindow(w.i, { end: v })} />
                        <IconButton size="small" onClick={() => removeWindow(w.i)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Paper>
                );
              })}
            </Box>
          )}

          {/* 주간 시간표 */}
          <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 1.5, userSelect: "none" }}>
            {/* 시간 축 확장 (위) */}
            {effStart > AXIS_MIN && (
              <Box sx={{ textAlign: "center" }}>
                <Button
                  size="small"
                  startIcon={<KeyboardArrowUp sx={{ fontSize: 14 }} />}
                  onClick={() => setAxisStart(Math.max(AXIS_MIN, effStart - 60))}
                  sx={{ fontSize: "0.75rem", py: 0, color: "text.secondary" }}
                >
                  이전 시간 보기 ({minToTime(Math.max(AXIS_MIN, effStart - 60))}~)
                </Button>
              </Box>
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: `52px repeat(${DAY_CODES.length}, 1fr)` }}>
              <Box />
              {DAY_CODES.map(([code, label]) => (
                <Typography key={code} variant="caption" textAlign="center" fontWeight={600}
                  color={code === "SAT" ? "primary.main" : "text.secondary"} sx={{ pb: 0.5, fontSize: "0.85rem" }}>
                  {label}
                </Typography>
              ))}

              {/* 시간축 라벨 */}
              <Box sx={{ position: "relative", height: axisHeight }}>
                {Array.from({ length: Math.floor((AXIS_END - effStart) / 60) + 1 }, (_, i) => {
                  const m = effStart + i * 60;
                  if (m > AXIS_END) return null;
                  return (
                    <Typography key={m} variant="caption" color="text.disabled"
                      sx={{ position: "absolute", top: yOf(m) - 8, right: 6, fontSize: "0.75rem" }}>
                      {minToTime(m)}
                    </Typography>
                  );
                })}
              </Box>

              {/* 요일 컬럼 */}
              {DAY_CODES.map(([code]) => (
                <Box key={code} sx={{
                  position: "relative", height: axisHeight,
                  borderLeft: "1px solid", borderColor: "divider", ...gridLineCss
                }}>
                  {selectedId == null ? renderOverviewDay(code) : renderEditDay(code)}
                </Box>
              ))}
            </Box>
            {selectedId != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                블록의 위/아래 가장자리를 드래그해 30분 단위로 조절할 수 있습니다. 빗금 블록은 저장 전 변경 사항입니다.
              </Typography>
            )}
          </Paper>
        </Stack>
      )}

      <SaveBar dirty={dirty} saving={saving} onSave={() => save()} onReset={resetWorking} />
    </Box>
  );
}
